import * as React from "react";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { HoldingTable, type HoldingRow } from "../HoldingTable";
import { useHoldings } from "@/hooks/queries/usePortfolio";
import { appConfig } from "@/utils/config";

interface HoldingPayload {
  instrument_token: string;
  quantity: number;
  average_price: number;
}

/**
 * Backend v2.1 (Sprint 4.2) `GET /api/v1/portfolio/holdings` - a genuine
 * read of already-persisted holdings, replacing the Sprint 3.3-era
 * permanently-unavailable placeholder. `exchange`/`current_price`/
 * `market_value` remain "Not reported by backend": `HoldingDto` carries
 * only `instrument_token`/`quantity`/`average_price` - rendering the
 * others would be fabricated.
 */
export function HoldingsSection(): React.JSX.Element {
  const holdingsQuery = useHoldings(appConfig.defaultPortfolioId);

  return (
    <StatusCard title="Holdings">
      <QueryStateBoundary
        query={holdingsQuery}
        isEmpty={(data) => (data as unknown as HoldingPayload[]).length === 0}
        emptyMessage="No holdings reported by the backend."
      >
        {(payload) => {
          const holdings = payload as unknown as HoldingPayload[];
          const rows: HoldingRow[] = holdings.map((holding) => ({
            instrument_token: holding.instrument_token,
            exchange: null,
            quantity: holding.quantity,
            average_price: holding.average_price,
            current_price: null,
            market_value: null,
          }));
          return <HoldingTable holdings={rows} />;
        }}
      </QueryStateBoundary>
    </StatusCard>
  );
}
